import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../context/LangContext';
import RichTextEditor from './RichTextEditor';

// Verzend-dialoog: mailt een bewaard rapport als bijlage.
// props: report { id, audit_id, original_filename }, clientId, onClose, onSent
export default function SendReportMailDialog({ report, clientId, onClose, onSent }) {
  const { t } = useLang();
  const [contacts, setContacts]     = useState([]);
  const [templates, setTemplates]   = useState([]);
  const [to, setTo]                 = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [cc, setCc]                 = useState('');
  const [bcc, setBcc]               = useState('');
  const [replyTo, setReplyTo]       = useState('');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject]       = useState('');
  const [bodyHtml, setBodyHtml]     = useState('');
  const [preview, setPreview]       = useState(null);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    Promise.all([
      clientId ? api.get(`/api/clients/${clientId}/contacts`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      api.get('/api/mail/templates').catch(() => ({ data: [] })),
      api.get('/api/mail/settings').catch(() => ({ data: {} })),
    ]).then(([c, tpl, s]) => {
      setContacts(c.data);
      setTemplates(tpl.data);
      setCc(s.data?.default_cc || '');
      setBcc(s.data?.default_bcc || '');
      setReplyTo(s.data?.default_reply_to || '');
      const def = tpl.data.find(x => x.is_default);
      if (def) applyTemplate(def);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  function applyTemplate(tpl) {
    setTemplateId(String(tpl.id));
    setSubject(tpl.subject);
    setBodyHtml(tpl.body_html);
  }

  function onSelectTemplate(id) {
    const tpl = templates.find(x => String(x.id) === String(id));
    if (tpl) applyTemplate(tpl);
    else { setTemplateId(''); }
  }

  function onSelectContact(email) {
    setTo(email);
    const c = contacts.find(x => x.email === email);
    setRecipientName(c ? c.name : '');
  }

  async function doPreview() {
    setError('');
    try {
      const res = await api.post('/api/mail/preview', {
        subject, body_html: bodyHtml,
        audit_id: report.audit_id, report_id: report.id, recipient_name: recipientName,
      });
      setPreview(res.data);
    } catch {
      setError(t('error'));
    }
  }

  async function doSend() {
    if (!to) { setError(t('mail_to_required')); return; }
    setSending(true); setError('');
    try {
      await api.post(`/api/reports/${report.id}/mail`, {
        to, recipient_name: recipientName, cc, bcc, reply_to: replyTo, subject, body_html: bodyHtml,
      });
      onSent && onSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || t('error'));
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title"><i className="bi bi-envelope me-2" />{t('mail_send_title')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={sending} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="mb-2 text-muted small">
                <i className="bi bi-paperclip me-1" />{report.original_filename}
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-6">
                  <label className="form-label small fw-medium">{t('mail_to')} *</label>
                  {contacts.length > 0 && (
                    <select className="form-select form-select-sm mb-1"
                      value={contacts.some(c => c.email === to) ? to : ''}
                      onChange={e => onSelectContact(e.target.value)}>
                      <option value="">{t('mail_pick_contact')}</option>
                      {contacts.filter(c => c.email).map(c => (
                        <option key={c.id} value={c.email}>{c.name} ({c.email})</option>
                      ))}
                    </select>
                  )}
                  <input className="form-control form-control-sm" type="email" placeholder="naam@voorbeeld.be"
                    value={to} onChange={e => { setTo(e.target.value); }} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium">{t('mail_template')}</label>
                  <select className="form-select form-select-sm" value={templateId}
                    onChange={e => onSelectTemplate(e.target.value)}>
                    <option value="">{t('mail_no_template')}</option>
                    {templates.map(tpl => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="row g-2 mb-2">
                <div className="col-md-4">
                  <label className="form-label small">Cc</label>
                  <input className="form-control form-control-sm" value={cc} onChange={e => setCc(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Bcc</label>
                  <input className="form-control form-control-sm" value={bcc} onChange={e => setBcc(e.target.value)} />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Reply-To</label>
                  <input className="form-control form-control-sm" value={replyTo} onChange={e => setReplyTo(e.target.value)} />
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label small fw-medium">{t('mail_subject')}</label>
                <input className="form-control form-control-sm" value={subject} onChange={e => setSubject(e.target.value)} />
              </div>
              <div className="mb-2">
                <label className="form-label small fw-medium">{t('mail_body')}</label>
                <RichTextEditor value={bodyHtml} onChange={setBodyHtml} minHeight={160} />
                <div className="form-text">{t('mail_merge_hint')}</div>
              </div>

              {preview && (
                <div className="card bg-light mb-1">
                  <div className="card-body py-2">
                    <div className="small text-muted">{t('mail_preview')}</div>
                    <div className="fw-medium small">{preview.subject}</div>
                    <div className="small" dangerouslySetInnerHTML={{ __html: preview.body_html }} />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={doPreview} disabled={sending}>
                <i className="bi bi-eye me-1" />{t('mail_preview')}
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} disabled={sending}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={doSend} disabled={sending || !to}>
                {sending && <span className="spinner-border spinner-border-sm me-2" />}
                <i className="bi bi-send me-1" />{t('mail_send')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}
